import { trackActivity } from '@/lib/productActivity';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ReactGA from 'react-ga4';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useRef } from 'react';
import type { Session } from '@supabase/supabase-js';
import { track } from '@/lib/analytics';
import { ensureWriteSession, isAuthRejected, SessionLostError, type SessionLostReason } from '@/lib/auth/writeSession';
import type { WizardRow, WizardStatusPayload, WizardStep, WizardStatus } from './steps';

export const WIZARD_QUERY_KEY = ['onboarding-wizard'] as const;

/**
 * The one server question the wizard asks: where am I, and should I be here?
 * Held for the session — every write below returns the fresh row and patches
 * the cache, so the answer never goes stale between steps.
 */
export function useWizardStatus(enabled = true) {
  const { user, session } = useAuth();
  const sessionRef = useRef(session);
  sessionRef.current = session;
  return useQuery({
    queryKey: [...WIZARD_QUERY_KEY, user?.id],
    enabled: enabled && !!user?.id,
    staleTime: Infinity,
    // A lost session is not retried blindly: the Welcome page offers Reconnect.
    retry: (count, e) => !isSessionLost(e) && count < 3,
    queryFn: () => fetchWizardStatus(user!.id, () => sessionRef.current),
  });
}

/**
 * Same guard as the writes (#1657): never ask as anon while the page shows a
 * signed-in owner, or the anon answer ("not eligible") sends them away.
 */
async function fetchWizardStatus(userId: string, getFallbackSession: () => Session | null): Promise<WizardStatusPayload> {
  const data = await sendAsUser(userId, getFallbackSession, (token) => supabase
    .rpc('onboarding_wizard_status')
    .setHeader('Authorization', `Bearer ${token}`));
  const payload = (data ?? {}) as Partial<WizardStatusPayload>;
  return {
    eligible: payload.eligible === true,
    wizard: (payload.wizard as WizardRow | null) ?? null,
  };
}

export const fetchWizardStatusForTests = fetchWizardStatus;

export interface WizardPatch {
  current_step?: WizardStep;
  status?: WizardStatus;
  sample_used?: boolean;
  customer_id?: string | null;
  mockup_id?: string | null;
  quote_id?: string | null;
  proposal_id?: string | null;
  event?: { step: WizardStep; action: 'enter' | 'complete' | 'skip' | 'dismiss' | 'resume' | 'error' };
}

/**
 * Why a wizard write did not land. `session_lost` means it was never sent (or
 * was refused before the function ran) because this device no longer holds a
 * session for the person on screen — the Welcome page owns that message.
 */
export type WizardSaveErrorKind = 'session_lost' | 'forbidden' | 'network' | 'other';

export class WizardSaveError extends Error {
  readonly kind: WizardSaveErrorKind;
  readonly status: number | null;
  readonly reason: SessionLostReason | null;
  constructor(kind: WizardSaveErrorKind, message: string, opts: { status?: number | null; reason?: SessionLostReason | null } = {}) {
    super(message);
    this.name = 'WizardSaveError';
    this.kind = kind;
    this.status = opts.status ?? null;
    this.reason = opts.reason ?? null;
  }
}

export function isSessionLost(e: unknown): e is WizardSaveError {
  return e instanceof WizardSaveError && e.kind === 'session_lost';
}

function sessionLost(reason: SessionLostReason, status: number | null = null): WizardSaveError {
  return new WizardSaveError('session_lost', new SessionLostError(reason).message, { status, reason });
}

function classify(error: { message?: string; code?: string } | null, status: number): WizardSaveError {
  const message = error?.message || 'Progress could not be saved';
  if (status === 0 || /failed to fetch|networkerror|load failed|network request failed/i.test(message)) {
    return new WizardSaveError('network', message, { status });
  }
  if (status === 403) return new WizardSaveError('forbidden', message, { status });
  return new WizardSaveError('other', message, { status });
}

/**
 * Writes are serialized per signed-in user. Two saves fired back to back (skip
 * a step, then exit) otherwise race on the wire and the server applies them in
 * arrival order, which can be the wrong one — a late "enter brand" write
 * landing after "now on customer" drags the stored step backwards.
 *
 * Every job belongs to the user who queued it (#1657):
 * - it is sent only with THAT user's access token, set explicitly on the
 *   request, so it can never go out as anon or under another account;
 * - a sign-out or a sign-in by someone else bumps the user's generation, and a
 *   job from an older generation is dropped without being sent;
 * - before sending, ensureWriteSession restores a session that vanished from
 *   storage while the page still holds it (bounded, single-flight).
 */
const saveChains = new Map<string, Promise<unknown>>();
const generations = new Map<string, number>();
let authWatch: { unsubscribe: () => void } | null = null;

function bump(userId: string): void {
  generations.set(userId, (generations.get(userId) ?? 0) + 1);
}

function watchAuth(): void {
  if (authWatch) return;
  // Synchronous body only: calling the client inside this callback deadlocks auth-js.
  const { data } = supabase.auth.onAuthStateChange((event, session) => {
    const current = session?.user?.id ?? null;
    if (event !== 'SIGNED_OUT' && !current) return;
    for (const userId of new Set([...generations.keys(), ...saveChains.keys()])) {
      if (userId !== current) bump(userId);
    }
  });
  authWatch = data.subscription;
}

export interface WizardSaveDeps {
  /** React's in-memory session — the copy restored when storage loses it. */
  getFallbackSession: () => Session | null;
  /** Called after a silent restore let the write through. */
  onRecovered?: () => void;
}

/**
 * Queue one write for `userId`. Rejects with WizardSaveError; `session_lost`
 * rejections never reached the function body.
 */
type RpcReply = { data: unknown; error: { message?: string; code?: string } | null; status: number };

/**
 * Send one request as `userId`, with that user's access token set explicitly.
 * Resolves the session first (ensureWriteSession). A 401 proves the request
 * was not run as the user — the function body never ran — so it is re-sent
 * exactly once, after a forced server refresh of the session, never with the
 * token that was just refused. Network errors (outcome unknown) are never
 * re-sent. `beforeSend` may veto a send (the queue's generation check).
 */
async function sendAsUser(
  userId: string,
  getFallbackSession: () => Session | null,
  call: (accessToken: string) => PromiseLike<RpcReply>,
  hooks: { beforeSend?: () => void; onRecovered?: () => void } = {},
): Promise<unknown> {
  const attempt = async (forceRefresh: boolean): Promise<unknown> => {
    const resolved = await ensureWriteSession(userId, getFallbackSession(), { forceRefresh });
    if (resolved.ok === false) throw sessionLost((resolved as { reason: SessionLostReason }).reason);
    hooks.beforeSend?.();
    if (resolved.recovered) hooks.onRecovered?.();
    const { data, error, status } = await call(resolved.session.access_token);
    if (!error) return data;
    if (isAuthRejected(status)) throw sessionLost('unrecoverable', status);
    throw classify(error, status);
  };
  try {
    return await attempt(false);
  } catch (e) {
    if (isSessionLost(e) && e.status === 401) return await attempt(true);
    throw e;
  }
}

/**
 * Queue one write for `userId`. Rejects with WizardSaveError; `session_lost`
 * rejections never reached the function body.
 */
export function enqueueWizardSave(userId: string | null | undefined, patch: WizardPatch, deps: WizardSaveDeps): Promise<WizardRow> {
  if (!userId) return Promise.reject(sessionLost('signed_out'));
  watchAuth();
  const generation = generations.get(userId) ?? 0;
  const prev = saveChains.get(userId) ?? Promise.resolve();
  const run = prev.then(async () => (await sendAsUser(
    userId,
    deps.getFallbackSession,
    (token) => supabase
      .rpc('onboarding_wizard_save', { p_patch: patch as never })
      .setHeader('Authorization', `Bearer ${token}`),
    {
      beforeSend: () => {
        if ((generations.get(userId) ?? 0) !== generation) throw sessionLost('user_mismatch');
      },
      onRecovered: deps.onRecovered,
    },
  )) as WizardRow);
  saveChains.set(userId, run.catch(() => undefined));
  return run;
}

export function resetWizardSaveQueueForTests(): void {
  saveChains.clear();
  generations.clear();
  authWatch?.unsubscribe();
  authWatch = null;
}

type SaveResult = { row: WizardRow; userId: string };

/**
 * Every write goes through onboarding_wizard_save: it validates that artifact
 * ids belong to the caller's org and appends the funnel event. The cache is
 * patched from the returned row so the next render already knows.
 */
export function useSaveWizard() {
  const qc = useQueryClient();
  const { user, session } = useAuth();
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const userIdRef = useRef(user?.id ?? null);
  userIdRef.current = user?.id ?? null;
  return useMutation({
    mutationFn: async (patch: WizardPatch): Promise<SaveResult> => {
      // Captured now: the job belongs to whoever was signed in when it was queued.
      const userId = userIdRef.current;
      const row = await enqueueWizardSave(userId, patch, {
        getFallbackSession: () => sessionRef.current,
        onRecovered: () => track('wizard_session_recovered', { step: patchStep(patch), via: 'silent' }),
      });
      return { row, userId: userId! };
    },
    onError: (e: Error, patch) => {
      const err = e instanceof WizardSaveError ? e : classify({ message: e.message }, 0);
      // One event per refused or failed write, including session_lost writes
      // that a later Reconnect lands: recovery is measured by joining with
      // wizard_session_recovered, not by a field here. The raw server message
      // stays in telemetry only (toast.warning is not counted as error_shown).
      track('wizard_save_failed', {
        step: patchStep(patch), kind: err.kind, http_status: err.status, reason: err.reason, detail: err.message.slice(0, 200),
      });
      // A lost session is explained by the Welcome page's banner, not a toast.
      // An unsaved Exit is explained by the Welcome page as it leaves.
      if (err.kind === 'session_lost' || patch.status === 'dismissed') return;
      toast.warning(saveFailureCopy(err.kind));
    },
    onSuccess: ({ row, userId }, patch) => {
      qc.setQueryData<WizardStatusPayload>([...WIZARD_QUERY_KEY, userId], (old) => {
        // Saves fire without awaiting each other (a fast tap after a step
        // change), so responses can land out of order. The row's updated_at
        // is stamped by the server per write: never let an older answer
        // overwrite a newer one, or "dismissed" flips back to "active" and the
        // dashboard bounces the user straight back in.
        const stale = old?.wizard?.updated_at && row.updated_at && row.updated_at < old.wizard.updated_at;
        return { eligible: old?.eligible ?? true, wizard: stale ? old!.wizard : row };
      });
      if (patch.event) {
        trackWizard(`${patch.event.step}_${patch.event.action}`);
        trackActivity(`wizard_${patch.event.step}_${patch.event.action}`, { metadata: { to: row.current_step, status: row.status } });
      }
    },
  });
}

/** Customer-facing copy for a failed save: never the raw server text. */
export function saveFailureCopy(kind: WizardSaveErrorKind): string {
  if (kind === 'network') return 'Progress not saved — check your connection and try again.';
  if (kind === 'forbidden') return 'Progress not saved — only an organization admin can update setup.';
  return 'Progress not saved. Try again in a moment.';
}

function patchStep(patch: WizardPatch): WizardStep | null {
  return patch.event?.step ?? patch.current_step ?? null;
}

/** GA4 funnel events, best effort — never let analytics break a step. */
export function trackWizard(action: string, params: Record<string, string | number | boolean> = {}): void {
  try {
    if (!ReactGA.isInitialized) return;
    ReactGA.event('onboarding_wizard', { action, ...params });
  } catch {
    /* analytics is optional */
  }
}
